import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RappelDialog from "@/components/RappelDialog";
import WelcomeDialog from "@/components/WelcomeDialog";
import PfmpCountdown from "@/components/PfmpCountdown";
import CorrectedDocReminderBanner from "@/components/CorrectedDocReminderBanner";
import { StatusBadge, ProgressBar, LevelPill, LEVELS, BlocSwitcher, yearLabel, seanceIconClass } from "@/components/shared";
import {
  ArrowRight, BookOpen, Gamepad2, FolderOpen, StickyNote,
  Target, CheckCircle2, Loader2, PlayCircle, Flame,
} from "lucide-react";

const RAYONS = [
  { to: "/cours", icon: BookOpen, title: "Mes cours", desc: "Les séances de la séquence, à suivre dans l'ordre ou à revoir.", tag: "7 séances · C2.1 → C2.5", cta: "Entrer", color: "orange" },
  { to: "/entrainement", icon: Gamepad2, title: "M'entraîner", desc: "QCM auto-corrigés par compétence pour réviser et progresser.", tag: "Quiz dispo", cta: "Jouer", color: "sky" },
  { to: "/portfolio", icon: FolderOpen, title: "Mon portfolio", desc: "Ton suivi de compétences, fiche par fiche, du Novice à l'Expert.", tag: "Suivi CCF · 4 niveaux", cta: "Me positionner", color: "orange" },
  { to: "/memos", icon: StickyNote, title: "Mémos", desc: "Fiches repères : vocabulaire pro, règles DGCCRF, méthode de soutenance.", tag: "Fiches à imprimer", cta: "Consulter", color: "sky" },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const [sequences, setSequences] = useState([]);
  const [seqId, setSeqId] = useState(null);
  const [data, setData] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [welcomePending, setWelcomePending] = useState(() => sessionStorage.getItem("mz_first_login") === "1");

  useEffect(() => {
    api.get("/sequences").then(({ data }) => {
      setSequences(data);
      setSeqId((prev) => prev || data[0]?.id);
    });
    api.get("/portfolio").then(({ data }) => setPortfolio(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!seqId) return;
    setData(null);
    api.get(`/dashboard?sequence_id=${seqId}`).then(({ data }) => setData(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seqId]);

  if (!data) {
    return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 text-orange-400 animate-spin" /></div>;
  }

  const { sequence, seances, current_seance, passed, todo, total } = data;
  const targetIndex = LEVELS.findIndex((l) => l.label === sequence.level_target_year);
  const blocPrefix = "C" + (sequence.bloc.split(" ")[1] || "");
  const blocPortfolio = portfolio.filter((c) => c.code.startsWith(blocPrefix));
  const coursCodes = [...new Set(seances.map((s) => s.competency_code))].sort();
  const coursTag = `${total} séances · ${coursCodes[0]} → ${coursCodes[coursCodes.length - 1]}`;
  const segClass = (n, level) => {
    if (n <= level) return "bg-orange-500";
    if (n <= targetIndex + 1) return "bg-orange-100";
    return "bg-slate-100";
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-8 mz-rise">
      {user?.role === "student" && welcomePending && <WelcomeDialog onClose={() => setWelcomePending(false)} />}
      {user?.role === "student" && !welcomePending && <RappelDialog />}
      {/* greeting */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-orange-500 uppercase tracking-wide">{sequence.bloc} · {sequence.bloc_title}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1">
            Salut {user?.name?.split(" ")[0]} 👋
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "student" && data.streak_weeks >= 2 && (
            <span
              data-testid="streak-badge"
              title={`${data.streak_weeks} semaines de dépôt d'affilée`}
              className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full text-sm font-extrabold shrink-0"
            >
              <Flame className="h-4 w-4 fill-orange-500 text-orange-600" /> {data.streak_weeks} semaines en série
            </span>
          )}
          <BlocSwitcher sequences={sequences} value={seqId} onChange={setSeqId} />
        </div>
      </div>

      {user?.role === "student" && <PfmpCountdown />}
      {user?.role === "student" && <CorrectedDocReminderBanner />}

      {/* Vitrine de la semaine */}
      <section className="rounded-3xl bg-white border border-slate-100 shadow-sm overflow-hidden" data-testid="vitrine-semaine">
        <div className="grid lg:grid-cols-3">
          <div className="lg:col-span-2 p-7 sm:p-9">
            <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wide">
              ★ Vitrine de la semaine
            </span>
            <div className="flex items-center gap-2 mt-4 text-sm font-bold text-slate-400">
              <span>Séance {current_seance.order} / {total}</span>
              <span>·</span>
              <span className="text-slate-500">{current_seance.competency_code}</span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">{current_seance.title}</h2>
            <p className="text-slate-600 mt-3 font-medium leading-relaxed max-w-2xl">{current_seance.objective}</p>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <Link to="/cours" data-testid="open-seance-btn" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-3 font-bold transition-colors shadow-sm shadow-orange-200">
                <PlayCircle className="h-5 w-5" /> Ouvrir la séance
              </Link>
              <Link to="/cours" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900 font-bold px-4 py-3">
                Voir les {total} séances <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="bg-slate-50 border-t lg:border-t-0 lg:border-l border-slate-100 p-7 sm:p-9 flex flex-col justify-center gap-6">
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Niveau visé cette semaine</p>
              <p className="font-display text-xl font-semibold text-slate-900 mt-1">{current_seance.level_target}</p>
            </div>
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-2">Avancement séquence</p>
              <ProgressBar value={current_seance.order} max={total} />
              <p className="text-sm font-bold text-slate-500 mt-2">{passed} passée{passed > 1 ? "s" : ""} · {todo} à venir</p>
            </div>
          </div>
        </div>
      </section>

      {/* Les rayons */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl font-semibold text-slate-900">Les rayons</h2>
            <p className="text-slate-500 font-medium text-sm">Où aller, et pour quoi faire.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {RAYONS.map((r) => (
            <Link
              key={r.to}
              to={r.to}
              data-testid={`rayon-${r.to.slice(1)}`}
              className="group rounded-3xl bg-white border border-slate-100 shadow-sm p-6 flex flex-col hover:shadow-md hover:-translate-y-1 transition-all duration-200"
            >
              <div className={`h-12 w-12 rounded-2xl flex items-center justify-center mb-4 ${r.color === "orange" ? "bg-orange-100 text-orange-600" : "bg-sky-100 text-sky-600"}`}>
                <r.icon className="h-6 w-6" />
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900">{r.title}</h3>
              <p className="text-sm text-slate-500 font-medium mt-1 flex-1">{r.desc}</p>
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mt-4">{r.to === "/cours" ? coursTag : r.tag}</p>
              <span className={`inline-flex items-center gap-1.5 font-bold mt-2 ${r.color === "orange" ? "text-orange-600" : "text-sky-600"}`}>
                {r.cta} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* La séquence en cours */}
        <section className="lg:col-span-3">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-1">La séquence en cours</h2>
          <p className="text-slate-500 font-medium text-sm mb-4">{sequence.subtitle}</p>
          <div className="rounded-3xl bg-white border border-slate-100 shadow-sm divide-y divide-slate-100">
            {seances.map((s) => (
              <div key={s.id} className="flex items-center gap-4 p-4 sm:px-6">
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 text-sm font-extrabold ${seanceIconClass(s.status)}`}>
                  {s.status === "vue" ? <CheckCircle2 className="h-5 w-5" /> : s.order}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-slate-400">Semaine {s.order} · {s.competency_code}</p>
                  <p className="font-bold text-slate-800 truncate">{s.title}</p>
                </div>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        </section>

        {/* Ma progression */}
        <section className="lg:col-span-2">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-1">Ma progression</h2>
          <p className="text-slate-500 font-medium text-sm mb-4">
            Plafond visé en {yearLabel(user?.year)} : <span className="text-orange-600 font-bold">{sequence.level_target_year}</span>
          </p>
          <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 space-y-4">
            {blocPortfolio.map((c) => (
              <div key={c.code}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-bold text-slate-700">{c.code} · {c.short}</span>
                  <span className="text-xs font-extrabold text-slate-400">{LEVELS[c.level - 1].code}</span>
                </div>
                <div className="flex gap-1.5">
                  {LEVELS.map((l) => (
                    <div
                      key={l.n}
                      className={`flex-1 h-2.5 rounded-full ${segClass(l.n, c.level)}`}
                      title={l.label}
                    />
                  ))}
                </div>
              </div>
            ))}
            <Link to="/portfolio" data-testid="goto-portfolio" className="inline-flex items-center gap-1.5 text-orange-600 font-bold pt-1">
              <Target className="h-4 w-4" /> Me positionner
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
